import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Task } from 'src/app/_models/task';
import { BraskemPedagioResponse } from 'src/app/_models/braskem-pedagio';
import { environment } from 'src/environments/environment';
import { Pendency } from '../_models/pendency';
import { RasterSmStatusViagem, Travel } from '../_models/travel';
import { TravelsData } from '../_models/travels-data';
import { TripFlow } from '../_models/tripFlow';

export interface ReplaceTripRequest {
    romaneioAtual: string;
    romaneioNovo: string;
}

export interface UpdateTripRequest {
    idUserApp: string;
    idExternal: string;
    truckId: string;
    PLACA_CARRETA_1: string;
    PLACA_CARRETA_2: string;
}

@Injectable({
    providedIn: 'root',
})
export class TravelService {
    constructor(private http: HttpClient) { }

    // Preserva a viagem atual; as validações e a exclusão do duplicado são feitas pela API.
    replace(data: ReplaceTripRequest): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/replace`, data);
    }

    update(tripId: string, data: UpdateTripRequest): Observable<any> {
        return this.http.put<any>(
            `${environment.URL_API}/trip/update/${encodeURIComponent(tripId)}`, data);
    }

    getAllByStatus(status: 'Active' | 'Finished', idUser: string): Observable<Travel[]> {
        return this.http.get<any[]>(
            `${environment.URL_API}/trip/listAll/${status}/${idUser}`);
    }

    getFinishedYearsByUser(idUser: string): Observable<number[]> {
        return this.http.get<number[]>(
            `${environment.URL_API}/Trip/ListAll/Finished/ano/${idUser}`);
    }

    getFinishedByYear(idUser: string, ano: number): Observable<Travel[]> {
        return this.http.get<Travel[]>(
            `${environment.URL_API}/Trip/ListAll/Finished/${idUser}/${ano}`);
    }

    getFinishedByYearMonth(idUser: string, ano: number, mes: number): Observable<Travel[]> {
        return this.http.get<Travel[]>(
            `${environment.URL_API}/Trip/ListAll/Finished/${idUser}/${ano}/${mes}`);
    }

    getActiveByStatus(status: boolean, idUser: string): Observable<Travel[]> {
        let path: string = idUser;

        if (status != null) {
            path = path + `?startedRoute=${status}`;
        }

        return this.http.get<any[]>(
            `${environment.URL_API}/trip/listAll/Active/${path}`);
    }

    getTravelMapData(idUser: string): Observable<TravelsData> {
        return this.http.get<any>(
            `${environment.URL_API}/trip/tripMapData/${idUser}`);
    }

    getTripByUserApp(idUserApp: string): Observable<Travel | Travel[]> {
        return this.http.get<Travel | Travel[]>(
            `${environment.URL_API}/Trip?idUserApp=${idUserApp}`);
    }

    getByTripId(idTrip: string): Observable<Travel> {
        return this.http.get<Travel>(
            `${environment.URL_API}/Trip/TripId/${encodeURIComponent(idTrip)}`);
    }

    getDocuments(idTrip: string): Observable<TripFlow[]> {
        return this.http.get<any>(
            `${environment.URL_API}/trip/tripFlow/documents/${idTrip}`);
    }

    consultarPedagioBraskem(documentoTransporte: string): Observable<BraskemPedagioResponse> {
        return this.http.post<BraskemPedagioResponse>(
            `${environment.URL_API}/Braskem/Pedagio`,
            {
                DOC_TRANSPORTE: documentoTransporte
            });
    }

    getRasterSmStatusViagem(identificacao: string): Observable<RasterSmStatusViagem> {
        return this.http.get<RasterSmStatusViagem>(
            `${environment.URL_API}/Raster/SM/StatusViagem/to/${encodeURIComponent(identificacao)}`);
    }

    getLoadingLocation(idTrip: string): Observable<any> {
        return this.http.get<any>(
            `${environment.URL_API}/trip/loadinglocation/${idTrip}`);
    }

    uploadDocument(idTripFlow: string, idUser: string, data: FormData): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/uploadDocuments/${idTripFlow}/${idUser}`, data);
    }

    uploadChecklistCode(idTripFlow: string, checklistCode: string): Observable<any> {
        const data = new FormData();
        data.append('checklistCode', checklistCode);
        return this.http.post(`${environment.URL_API}/Trip/UploadChecklistCode/${idTripFlow}`, data);
    }

    changeStatus(idTripFlow: string, status: 'Send' | 'Done' | 'Disapprove'): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/changeStatus/${status}/${idTripFlow}`, null);
    }

    reverseTripFlow(idTripFlow: string): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/Trip/TripFlow/Reverse/${idTripFlow}`, null);
    }

    deleteDocument(idDocument: string): Observable<any> {
        return this.http.delete<any>(
            `${environment.URL_API}/documenttrip/${idDocument}`);
    }

    checkPendency(idTripFlow: string, idUser: string): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/checkPendency?idTrip=${idTripFlow}&idUser=${idUser}`, null);
    }

    getPendency(idUser: string, startDate: string, endDate: string): Observable<Pendency> {
        return this.http.get<Pendency>(
            `${environment.URL_API}/trip/allpendency/${idUser}/${startDate}/${endDate}`);
    }

    getPendencyDetailByUser(idUser: string, startDate: string, endDate: string): Observable<Task[]> {
        return this.http.get<Task[]>(
            `${environment.URL_API}/trip/pendencydetailbyuser/${idUser}/${startDate}/${endDate}`);
    }

    getPendencyDetailByCompany(companyCnpj: string, idUser: string, startDate: string, endDate: string): Observable<Task[]> {
        return this.http.get<Task[]>(
            `${environment.URL_API}/trip/pendencydetailbycompany/${companyCnpj}/${idUser}/${startDate}/${endDate}`);
    }

    getPendencyDetailByDocument(idDocument: string, idUser: string, startDate: string, endDate: string): Observable<Task[]> {
        return this.http.get<Task[]>(
            `${environment.URL_API}/trip/pendencydetailbydocument/${idDocument}/${idUser}/${startDate}/${endDate}`);
    }

    finish(idTrip: string): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/finish/${idTrip}`, null);
    }

    start(idTrip: string): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/Trip/Start/${idTrip}`, null);
    }

    checkTripFlowFinished(idTrip: string) {
        return this.http.get<boolean>(
            `${environment.URL_API}/trip/CheckTripFlowFinished/${idTrip}`);
    }

    cancel(idTrip: string, justification: string): Observable<any> {
        return this.http.post<any>(
            `${environment.URL_API}/trip/Cancel/${idTrip}`,
            { justification } 
        );
    }

}
